import { CopyButton } from "@components/shared/CopyButton";
import {
  CREATE_TARGET_COMPANY,
  CREATE_TARGET_SECTOR,
  DELETE_TARGET_COMPANY,
  DELETE_TARGET_SECTOR,
  GENERATE_BOOLEAN_STRINGS,
  SAVE_SEARCH_QUERY,
  UPDATE_TARGET_COMPANY,
  UPDATE_TARGET_SECTOR,
} from "@lib/graphql/mutations";
import { MARKET_MAP_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import {
  ArrowRight,
  Ban,
  Building2,
  Circle,
  ExternalLink,
  MapPin,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useCallback, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "urql";
import { CompanyForm } from "./CompanyForm";
import { SectorForm } from "./SectorForm";

interface MarketMappingStageProps {
  campaignId: string;
}

interface TargetSector {
  id: string;
  name: string;
  relevance: string;
  position: number;
  notes: string | null;
  excluded: boolean;
  targetCompanies: { id: string; name: string }[];
}

interface TargetCompany {
  id: string;
  name: string;
  relevance: string;
  employeeCountRange: string | null;
  website: string | null;
  notes: string | null;
  excluded: boolean;
  targetSector: { id: string; name: string } | null;
}

const RELEVANCE_COLORS: Record<string, string> = {
  high: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  low: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};

type LinkedinAudience = "recruiter" | "basic" | "xray";
type LinkedinField = "title" | "company" | "keywords";

const XRAY_PREFIX = "site:linkedin.com/in/";

function googleSearchUrl(query: string): string {
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

interface SearchQuery {
  id: string;
  query_string: string;
  query_type: string;
  description: string | null;
  linkedin_audience: LinkedinAudience | null;
  linkedin_field: LinkedinField | null;
  created_by: string;
  created_at: string;
}

const AUDIENCE_LABELS: Record<LinkedinAudience, string> = {
  recruiter: "Recruiter / Sales Nav",
  basic: "Basic LinkedIn",
  xray: "X-Ray (Google)",
};

const AUDIENCE_DESCRIPTIONS: Record<LinkedinAudience, string> = {
  recruiter:
    "Compound booleans designed for the Title / Company / Keywords fields in LinkedIn Recruiter or Sales Navigator.",
  basic:
    "Single-string queries for the global LinkedIn search bar. Matches against the whole profile, so anchor with sector + skill terms, not just titles.",
  xray: "Google searches restricted to public LinkedIn profiles via site:linkedin.com/in/. Click any query to run it on Google. Useful when LinkedIn access is limited.",
};

const FIELD_CHIP_CLASSES: Record<LinkedinField, string> = {
  title: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  company: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  keywords: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
};

export function MarketMappingStage({ campaignId }: MarketMappingStageProps) {
  const [addingSector, setAddingSector] = useState(false);
  const [editingSectorId, setEditingSectorId] = useState<string | null>(null);
  const [addingCompany, setAddingCompany] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);
  const [addingQuery, setAddingQuery] = useState(false);
  const [queryAudienceTab, setQueryAudienceTab] = useState<LinkedinAudience>("recruiter");
  const [queryForm, setQueryForm] = useState<{
    queryString: string;
    queryType: string;
    description: string;
    linkedinAudience: LinkedinAudience;
    linkedinField: LinkedinField;
  }>({
    queryString: "",
    queryType: "boolean",
    description: "",
    linkedinAudience: "recruiter",
    linkedinField: "title",
  });

  const [{ data, fetching }, reexecute] = useQuery({
    query: MARKET_MAP_QUERY,
    variables: { id: campaignId },
  });
  const [{ fetching: creatingSector }, createSector] = useMutation(CREATE_TARGET_SECTOR);
  const [, updateSector] = useMutation(UPDATE_TARGET_SECTOR);
  const [, deleteSector] = useMutation(DELETE_TARGET_SECTOR);
  const [{ fetching: creatingCompany }, createCompany] = useMutation(CREATE_TARGET_COMPANY);
  const [, updateCompany] = useMutation(UPDATE_TARGET_COMPANY);
  const [, deleteCompany] = useMutation(DELETE_TARGET_COMPANY);
  const [{ fetching: savingQuery }, saveSearchQuery] = useMutation(SAVE_SEARCH_QUERY);
  const [{ fetching: generatingQueries }, generateBooleanStrings] =
    useMutation(GENERATE_BOOLEAN_STRINGS);
  const [generateMessage, setGenerateMessage] = useState<string | null>(null);

  const marketMap = data?.campaign?.marketMap;
  const sectors: TargetSector[] = marketMap?.targetSectors ?? [];
  const companies: TargetCompany[] = marketMap?.targetCompanies ?? [];
  const searchQueries: SearchQuery[] = marketMap?.searchQueries ?? [];

  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const handleCreateSector = async (data: { name: string; relevance: string; notes: string }) => {
    await createSector({ input: { campaignId, ...data } });
    setAddingSector(false);
    refresh();
  };

  const handleUpdateSector = async (
    id: string,
    data: { name: string; relevance: string; notes: string },
  ) => {
    await updateSector({ input: { id, ...data } });
    setEditingSectorId(null);
    refresh();
  };

  const handleDeleteSector = async (id: string) => {
    await deleteSector({ input: { id } });
    refresh();
  };

  const handleToggleSectorExcluded = async (id: string, excluded: boolean) => {
    await updateSector({ input: { id, excluded: !excluded } });
    refresh();
  };

  const handleCreateCompany = async (data: {
    name: string;
    relevance: string;
    employeeCountRange: string;
    website: string;
    notes: string;
    targetSectorId: string;
  }) => {
    const input: Record<string, string> = {
      campaignId,
      name: data.name,
      relevance: data.relevance,
    };
    if (data.employeeCountRange) input.employeeCountRange = data.employeeCountRange;
    if (data.website) input.website = data.website;
    if (data.notes) input.notes = data.notes;
    if (data.targetSectorId) input.targetSectorId = data.targetSectorId;
    await createCompany({ input });
    setAddingCompany(false);
    refresh();
  };

  const handleUpdateCompany = async (
    id: string,
    data: {
      name: string;
      relevance: string;
      employeeCountRange: string;
      website: string;
      notes: string;
      targetSectorId: string;
    },
  ) => {
    const input: Record<string, string> = { id, name: data.name, relevance: data.relevance };
    if (data.employeeCountRange) input.employeeCountRange = data.employeeCountRange;
    if (data.website) input.website = data.website;
    if (data.notes) input.notes = data.notes;
    if (data.targetSectorId) input.targetSectorId = data.targetSectorId;
    await updateCompany({ input });
    setEditingCompanyId(null);
    refresh();
  };

  const handleDeleteCompany = async (id: string) => {
    await deleteCompany({ input: { id } });
    refresh();
  };

  const handleToggleCompanyExcluded = async (id: string, excluded: boolean) => {
    await updateCompany({ input: { id, excluded: !excluded } });
    refresh();
  };

  const handleGenerateQueries = async () => {
    setGenerateMessage(null);
    const res = await generateBooleanStrings({ input: { campaignId } });
    const payload = res.data?.generateBooleanStrings;
    if (payload?.enqueued) {
      setGenerateMessage("Generation started — queries will appear shortly.");
    } else if (payload?.errors?.length) {
      setGenerateMessage(payload.errors.join(" "));
    } else if (res.error) {
      setGenerateMessage(res.error.message);
    }
  };

  const handleSaveQuery = async () => {
    const input: Record<string, string> = {
      campaignId,
      queryString: queryForm.queryString,
      queryType: queryForm.queryType,
    };
    if (queryForm.description) input.description = queryForm.description;
    if (queryForm.queryType === "boolean") {
      input.linkedinAudience = queryForm.linkedinAudience;
      if (queryForm.linkedinAudience === "recruiter") {
        input.linkedinField = queryForm.linkedinField;
      }
    }
    await saveSearchQuery({ input });
    setQueryForm({
      queryString: "",
      queryType: "boolean",
      description: "",
      linkedinAudience: queryAudienceTab,
      linkedinField: "title",
    });
    setAddingQuery(false);
    refresh();
  };

  if (fetching && marketMap === undefined) {
    return <p className="text-sm text-text-muted">Loading market map...</p>;
  }

  return (
    <div className="space-y-6">
      {/* Sectors */}
      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-text-faint" />
            <h3 className="text-sm font-medium text-text-secondary">Target Sectors</h3>
            {sectors.length > 0 && (
              <span className="text-xs text-text-faint">{sectors.length}</span>
            )}
          </div>
          {!addingSector && (
            <button
              type="button"
              onClick={() => setAddingSector(true)}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
            >
              <Plus size={12} /> Add Sector
            </button>
          )}
        </div>

        {addingSector && (
          <div className="mb-3">
            <SectorForm
              onSave={handleCreateSector}
              onCancel={() => setAddingSector(false)}
              saving={creatingSector}
            />
          </div>
        )}

        {sectors.length === 0 && !addingSector ? (
          <p className="py-4 text-center text-sm text-text-faint">No target sectors defined yet.</p>
        ) : (
          <div className="space-y-2">
            {sectors.map((sector) =>
              editingSectorId === sector.id ? (
                <SectorForm
                  key={sector.id}
                  initial={{
                    name: sector.name,
                    relevance: sector.relevance,
                    notes: sector.notes ?? "",
                  }}
                  onSave={(data) => handleUpdateSector(sector.id, data)}
                  onCancel={() => setEditingSectorId(null)}
                  saving={false}
                />
              ) : (
                <div
                  key={sector.id}
                  className={`flex items-center justify-between rounded border border-border px-3 py-2 ${sector.excluded ? "opacity-50" : ""}`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`text-sm font-medium text-text-primary ${sector.excluded ? "line-through" : ""}`}
                    >
                      {sector.name}
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${RELEVANCE_COLORS[sector.relevance]}`}
                    >
                      {sector.relevance}
                    </span>
                    {sector.targetCompanies.length > 0 && (
                      <span className="text-xs text-text-faint">
                        {sector.targetCompanies.length} companies
                      </span>
                    )}
                    {sector.notes && (
                      <span className="text-xs text-text-faint">{sector.notes}</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      title={sector.excluded ? "Include in searches" : "Exclude from searches"}
                      onClick={() => handleToggleSectorExcluded(sector.id, sector.excluded)}
                      className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                    >
                      {sector.excluded ? <Circle size={12} /> : <Ban size={12} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingSectorId(sector.id)}
                      className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteSector(sector.id)}
                      className="rounded p-1 text-text-faint hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </div>

      {/* Companies */}
      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 size={16} className="text-text-faint" />
            <h3 className="text-sm font-medium text-text-secondary">Target Companies</h3>
            {companies.length > 0 && (
              <span className="text-xs text-text-faint">{companies.length}</span>
            )}
          </div>
          {!addingCompany && (
            <button
              type="button"
              onClick={() => setAddingCompany(true)}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
            >
              <Plus size={12} /> Add Company
            </button>
          )}
        </div>

        {addingCompany && (
          <div className="mb-3">
            <CompanyForm
              sectors={sectors}
              onSave={handleCreateCompany}
              onCancel={() => setAddingCompany(false)}
              saving={creatingCompany}
            />
          </div>
        )}

        {companies.length === 0 && !addingCompany ? (
          <p className="py-4 text-center text-sm text-text-faint">No target companies added yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-text-muted">
                <th className="pb-2 font-medium">Company</th>
                <th className="pb-2 font-medium">Relevance</th>
                <th className="pb-2 font-medium">Sector</th>
                <th className="pb-2 font-medium">Employees</th>
                <th className="pb-2 font-medium">Website</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {companies.map((company) =>
                editingCompanyId === company.id ? (
                  <tr key={company.id}>
                    <td colSpan={6} className="py-2">
                      <CompanyForm
                        sectors={sectors}
                        initial={{
                          name: company.name,
                          relevance: company.relevance,
                          employeeCountRange: company.employeeCountRange ?? "",
                          website: company.website ?? "",
                          notes: company.notes ?? "",
                          targetSectorId: company.targetSector?.id ?? "",
                        }}
                        onSave={(data) => handleUpdateCompany(company.id, data)}
                        onCancel={() => setEditingCompanyId(null)}
                        saving={false}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={company.id}
                    className={`border-b border-border ${company.excluded ? "opacity-50" : ""}`}
                  >
                    <td
                      className={`py-2 font-medium text-text-primary ${company.excluded ? "line-through" : ""}`}
                    >
                      {company.name}
                    </td>
                    <td className="py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${RELEVANCE_COLORS[company.relevance]}`}
                      >
                        {company.relevance}
                      </span>
                    </td>
                    <td className="py-2 text-text-muted">{company.targetSector?.name ?? "—"}</td>
                    <td className="py-2 text-text-muted">{company.employeeCountRange ?? "—"}</td>
                    <td className="py-2 text-text-muted">{company.website ?? "—"}</td>
                    <td className="py-2">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          title={company.excluded ? "Include in searches" : "Exclude from searches"}
                          onClick={() => handleToggleCompanyExcluded(company.id, company.excluded)}
                          className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                        >
                          {company.excluded ? <Circle size={12} /> : <Ban size={12} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingCompanyId(company.id)}
                          className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCompany(company.id)}
                          className="rounded p-1 text-text-faint hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-900/20"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Search Queries */}
      <SearchQueriesPanel
        campaignId={campaignId}
        searchQueries={searchQueries}
        audienceTab={queryAudienceTab}
        onSelectAudienceTab={(audience) => {
          setQueryAudienceTab(audience);
          setQueryForm((prev) => ({ ...prev, linkedinAudience: audience }));
        }}
        addingQuery={addingQuery}
        onStartAdd={() => {
          setQueryForm((prev) => ({ ...prev, linkedinAudience: queryAudienceTab }));
          setAddingQuery(true);
        }}
        onCancelAdd={() => setAddingQuery(false)}
        queryForm={queryForm}
        onChangeQueryForm={setQueryForm}
        onSaveQuery={handleSaveQuery}
        savingQuery={savingQuery}
        onGenerate={handleGenerateQueries}
        generating={generatingQueries}
        generateMessage={generateMessage}
      />
    </div>
  );
}

type QueryFormState = {
  queryString: string;
  queryType: string;
  description: string;
  linkedinAudience: LinkedinAudience;
  linkedinField: LinkedinField;
};

interface SearchQueriesPanelProps {
  campaignId: string;
  searchQueries: SearchQuery[];
  audienceTab: LinkedinAudience;
  onSelectAudienceTab: (audience: LinkedinAudience) => void;
  addingQuery: boolean;
  onStartAdd: () => void;
  onCancelAdd: () => void;
  queryForm: QueryFormState;
  onChangeQueryForm: React.Dispatch<React.SetStateAction<QueryFormState>>;
  onSaveQuery: () => void | Promise<void>;
  savingQuery: boolean;
  onGenerate: () => void | Promise<void>;
  generating: boolean;
  generateMessage: string | null;
}

function inferAudience(q: SearchQuery): LinkedinAudience | null {
  if (q.linkedin_audience) return q.linkedin_audience;
  // Pre-backfill rows: an X-Ray query is unambiguous from its query string.
  if (q.query_string?.toLowerCase().startsWith(XRAY_PREFIX)) return "xray";
  // Other boolean rows default to recruiter.
  if (q.query_type === "boolean") return "recruiter";
  return null;
}

function SearchQueriesPanel({
  campaignId,
  searchQueries,
  audienceTab,
  onSelectAudienceTab,
  addingQuery,
  onStartAdd,
  onCancelAdd,
  queryForm,
  onChangeQueryForm,
  onSaveQuery,
  savingQuery,
  onGenerate,
  generating,
  generateMessage,
}: SearchQueriesPanelProps) {
  const audiences: LinkedinAudience[] = ["recruiter", "basic", "xray"];
  const countsByAudience: Record<LinkedinAudience, number> = {
    recruiter: searchQueries.filter((q) => inferAudience(q) === "recruiter").length,
    basic: searchQueries.filter((q) => inferAudience(q) === "basic").length,
    xray: searchQueries.filter((q) => inferAudience(q) === "xray").length,
  };
  const visibleQueries = searchQueries.filter((q) => inferAudience(q) === audienceTab);
  const missingAudiences = audiences.filter((aud) => countsByAudience[aud] === 0);
  // Only show the generate-missing affordance once at least one audience has
  // queries — first-run generation still flows through the StageActionBar.
  const canGenerate = missingAudiences.length > 0 && missingAudiences.length < audiences.length;
  const generateLabel =
    missingAudiences.length === 1
      ? `Generate ${AUDIENCE_LABELS[missingAudiences[0]]} queries`
      : "Generate queries";

  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Search size={16} className="text-text-faint" />
          <h3 className="text-sm font-medium text-text-secondary">Search Queries</h3>
          {searchQueries.length > 0 && (
            <span className="text-xs text-text-faint">{searchQueries.length}</span>
          )}
        </div>
        {!addingQuery && (
          <div className="flex items-center gap-1">
            {canGenerate && (
              <button
                type="button"
                onClick={onGenerate}
                disabled={generating}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Sparkles size={12} /> {generating ? "Generating…" : generateLabel}
              </button>
            )}
            <button
              type="button"
              onClick={onStartAdd}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
            >
              <Plus size={12} /> Add Query
            </button>
          </div>
        )}
      </div>

      {generateMessage && (
        <p className="mb-3 rounded border border-border bg-card-hover px-3 py-2 text-xs text-text-secondary">
          {generateMessage}
        </p>
      )}

      <div className="mb-3 flex gap-1 border-b border-border">
        {audiences.map((aud) => {
          const active = audienceTab === aud;
          return (
            <button
              key={aud}
              type="button"
              onClick={() => onSelectAudienceTab(aud)}
              className={`relative -mb-px border-b-2 px-3 py-1.5 text-xs font-medium ${
                active
                  ? "border-accent text-text-primary"
                  : "border-transparent text-text-muted hover:text-text-secondary"
              }`}
            >
              {AUDIENCE_LABELS[aud]}
              <span className="ml-1.5 text-text-faint">({countsByAudience[aud]})</span>
            </button>
          );
        })}
      </div>
      <div className="mb-3 flex items-center justify-between gap-4">
        <p className="text-xs text-text-muted">{AUDIENCE_DESCRIPTIONS[audienceTab]}</p>
        {audienceTab === "xray" && (
          <Link
            to={`/campaigns/${campaignId}/run/discovery`}
            className="flex flex-shrink-0 items-center gap-1 rounded px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/5"
          >
            Run X-Ray searches <ArrowRight size={12} />
          </Link>
        )}
      </div>

      {addingQuery && (
        <div className="mb-3 space-y-2 rounded border border-border p-3">
          <div className="flex flex-wrap gap-2">
            <select
              value={queryForm.queryType}
              onChange={(e) =>
                onChangeQueryForm((prev) => ({ ...prev, queryType: e.target.value }))
              }
              className="rounded border border-border bg-surface px-2 py-1 text-xs"
            >
              <option value="boolean">Boolean</option>
              <option value="semantic">Semantic</option>
            </select>
            {queryForm.queryType === "boolean" && (
              <select
                value={queryForm.linkedinAudience}
                onChange={(e) =>
                  onChangeQueryForm((prev) => ({
                    ...prev,
                    linkedinAudience: e.target.value as LinkedinAudience,
                  }))
                }
                className="rounded border border-border bg-surface px-2 py-1 text-xs"
                aria-label="LinkedIn audience"
              >
                <option value="recruiter">Recruiter / Sales Nav</option>
                <option value="basic">Basic LinkedIn</option>
                <option value="xray">X-Ray (Google)</option>
              </select>
            )}
            {queryForm.queryType === "boolean" && queryForm.linkedinAudience === "recruiter" && (
              <select
                value={queryForm.linkedinField}
                onChange={(e) =>
                  onChangeQueryForm((prev) => ({
                    ...prev,
                    linkedinField: e.target.value as LinkedinField,
                  }))
                }
                className="rounded border border-border bg-surface px-2 py-1 text-xs"
                aria-label="LinkedIn field"
              >
                <option value="title">Title field</option>
                <option value="company">Company field</option>
                <option value="keywords">Keywords field</option>
              </select>
            )}
            <input
              value={queryForm.description}
              onChange={(e) =>
                onChangeQueryForm((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="Description (optional)"
              className="min-w-[12rem] flex-1 rounded border border-border bg-surface px-2 py-1 text-xs"
            />
          </div>
          <textarea
            value={queryForm.queryString}
            onChange={(e) =>
              onChangeQueryForm((prev) => ({ ...prev, queryString: e.target.value }))
            }
            placeholder="Search query..."
            rows={2}
            className="w-full rounded border border-border bg-surface px-3 py-2 text-xs font-mono"
            required
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onSaveQuery}
              disabled={savingQuery || !queryForm.queryString}
              className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
            >
              {savingQuery ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={onCancelAdd}
              className="rounded border border-border px-3 py-1.5 text-xs text-text-muted hover:bg-card-hover"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {visibleQueries.length === 0 && !addingQuery ? (
        <p className="py-4 text-center text-sm text-text-faint">
          {countsByAudience[audienceTab] === 0
            ? `No ${AUDIENCE_LABELS[audienceTab]} queries saved yet.`
            : "No queries match the current filter."}
        </p>
      ) : (
        <div className="space-y-2">
          {visibleQueries.map((q) => (
            <div key={q.id} className="rounded border border-border px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                {q.linkedin_field && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      FIELD_CHIP_CLASSES[q.linkedin_field]
                    }`}
                  >
                    {q.linkedin_field}
                  </span>
                )}
                {q.query_type !== "boolean" && (
                  <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                    {q.query_type}
                  </span>
                )}
                {q.description && (
                  <span className="text-xs text-text-secondary">{q.description}</span>
                )}
                <span className="ml-auto text-[10px] text-text-faint">
                  {q.created_by} &middot; {new Date(q.created_at).toLocaleDateString()}
                </span>
              </div>
              <div className="mt-1 flex items-start gap-2">
                {inferAudience(q) === "xray" ? (
                  <a
                    href={googleSearchUrl(q.query_string)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Run on Google"
                    className="flex-1 font-mono text-xs text-accent hover:underline"
                  >
                    {q.query_string}
                    <ExternalLink size={11} className="ml-1 inline align-baseline" />
                  </a>
                ) : (
                  <p className="flex-1 font-mono text-xs text-text-primary">{q.query_string}</p>
                )}
                <CopyButton
                  ariaLabel="Copy search query"
                  size={12}
                  className="flex-shrink-0 rounded p-1 text-text-faint hover:bg-accent/5 hover:text-accent"
                  getContent={() => ({ html: q.query_string, text: q.query_string })}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
